import { defineStore } from "pinia";

import { config } from "@/config";

import { ModuleStatus } from "@/models/ModuleStatus";
import { BrandDocumentClassification } from "@/models/BrandDocumentClassification";

import { isApiError } from "@/services/Api";
import { recordReload } from "@/services/ReloadDiagnostics";
import {
  brandClientUiConfigService,
  brandService,
  recordEvidenceCountService,
  brandLensService,
  brandConfigService,
} from "@/services";

import { useFiltersStore } from "@/stores/filters-store";
import { useLoggingStore } from "@/stores/logging-store";
import { useSettingsStore } from "@/stores/settings-store";
import { useUserSessionStore } from "@/stores/user-session-store";
import { useBrandSecurityStore } from "@/stores/brand-security-store";
import { useUserPermissionStore } from "@/stores/user-permission-store";

import type { BrandLens } from "@/models/BrandLens";
import type { FullRetentionBrandConfig } from "@/services/BrandConfigurationService";
import type { BrandViewerConfig as BrandViewerUIConfig, LensConfig } from "@/models/BrandViewerConfig";

export enum BrandStatus {
  LoadingConfiguration = "Loading Configuration",
  NoAccess = "No Access",
  MissingBrand = "Missing Brand",
  MappingLoaded = "Mapping Loaded",
}

interface State {
  moduleStatus: BrandStatus | ModuleStatus;
  documentClassifications: BrandDocumentClassification[];
  __viewerConfig__?: BrandViewerUIConfig;
  evidenceCountLabels: string[];
  mappings: { [x: string]: string };
  lenses: BrandLens[];
  __lensConfig__?: LensConfig;
  __brandConfig__?: Partial<{ retention: FullRetentionBrandConfig }>;
}

export const RETENTION_STATUS_FIELD_PATH = "retention/retentionStatus";
export const RETENTION_EXPIRY_DATE_FIELD_PATH = "retention/effectiveExpiryDate";

const defaultLens = Object.freeze<BrandLens>({
  name: "Default",
  code: "def",
  order: 0,
  active: true,
});

const defaultBrandUIConfig = Object.freeze<BrandViewerUIConfig>({
  lens: {
    recordList: {
      autoTags: [],
      evidenceCounts: [],
      userTags: [],
      summariser: { enabled: false },
      ioa: { disabled: false },
    },
    timeline: {},
    evidenceThemes: {
      enableForUnknownClassifications: true,
      enableFilters: false,
      filters: [],
      highlightColors: {},
      hiddenThemes: {},
    },
    availableDialogueRoles: [],
    summaryThemes: {
      highlightColors: {},
    },
    features: {
      annotations: "enabled",
      evidence: "enabled",
      evidenceCounts: "enabled",
      ioa: "enabled",
      tags: "enabled",
      quarantine: "enabled",
      transcript: "enabled",
    },
    caseSearch: [],
  },
  apiVersion: "v1",
  polling: [],
  dataPoints: undefined,
  questionPack: { module: "generic" },
});

export const useBrandStore = defineStore("brand", {
  state: (): State => ({
    moduleStatus: ModuleStatus.Initial,
    documentClassifications: [],
    evidenceCountLabels: [],
    mappings: {},
    lenses: [defaultLens],
  }),
  getters: {
    classifications(): { [key: string]: BrandDocumentClassification } {
      return this.documentClassifications.reduce(
        (prev, next) => {
          return {
            ...prev,
            [next.name]: next,
          };
        },
        {} as Record<string, BrandDocumentClassification>,
      );
    },
    accessibleBrands(): string[] {
      if (!this.selectedClient) {
        return [];
      }

      return Object.entries(this.mappings).flatMap(([brand, client]) =>
        client === this.selectedClient ? brand.toLowerCase() : [],
      );
    },
    readyToLoad(): boolean {
      switch (this.moduleStatus) {
        case BrandStatus.NoAccess:
        case ModuleStatus.Ready:
        case BrandStatus.MissingBrand:
          return true;
        default:
          return false;
      }
    },
    apiVersion(): "v1" | "v2" {
      return this.viewerConfig.apiVersion || "v1";
    },
    selectedClient(): string | undefined {
      if (this.selectedBrand == null) return;
      return this.mappings[this.selectedBrand];
    },
    selectedBrand(): string | undefined {
      return (this.currentRoute.params.brand as string | undefined)?.toLowerCase();
    },
    selectedLens(): BrandLens {
      const lensCode = (this.currentRoute.query.lens as string | undefined)?.toLowerCase();
      if (!lensCode) {
        if (this.lenses.length > 0) return this.lenses[0];
        else return defaultLens;
      }

      return this.lenses.find((l) => l.code.toLowerCase() === lensCode) ?? defaultLens;
    },
    viewerConfig(): Omit<BrandViewerUIConfig, "lens"> {
      if (this.__viewerConfig__ == null) return defaultBrandUIConfig;
      return this.__viewerConfig__;
    },
    panelsConfig(): NonNullable<BrandViewerUIConfig["panels"]> {
      const config = this.viewerConfig.panels;
      return config == null || typeof config !== "object" || Array.isArray(config) ? {} : config;
    },
    // The brand screen's agent chat bubble — hidden, like the Agent Assistant panel, unless the brand's
    // viewer config says otherwise.
    agentChatBubbleEnabled(): boolean {
      return config.agent != null && (this.panelsConfig.agentAssistant?.state ?? "hidden") !== "hidden";
    },
    featuresConfig(): NonNullable<BrandViewerUIConfig["features"]> {
      const config = this.viewerConfig.features;
      return config == null || typeof config !== "object" || Array.isArray(config) ? {} : config;
    },
    caseSearchConfig(): NonNullable<BrandViewerUIConfig["caseSearch"]> {
      const config = this.viewerConfig.caseSearch;
      const parsed = Array.isArray(config) ? [...config] : [];

      // Set initial default column if none are provided, to ensure the case reference at least is always visible
      if (parsed.length === 0) {
        parsed.push({
          fieldPath: "caseReference",
          defaultVisible: true,
          displayName: "Case Ref.",
          filterable: false,
          searchable: false,
          sortable: false,
          visible: true,
        });
      }

      // Retention Column
      const brandRetentionConfig = this.brandConfig.retention;
      if (
        this.viewerConfig.retention?.showRetentionSection === true &&
        brandRetentionConfig.retentionFilters != null &&
        brandRetentionConfig.retentionFilters.length > 0
      ) {
        const retentionCaseSearchConfig = this.viewerConfig.retention.caseSearchFilters ?? {};

        const retentionFilterOptions = new Map<
          (typeof brandRetentionConfig.retentionFilters)[number]["key"],
          { label: string; value: string }
        >();
        for (const { key: value, value: label } of brandRetentionConfig.retentionFilters) {
          const isStatusActive = (() => {
            switch (value) {
              case "LegalHold":
                return retentionCaseSearchConfig.allowLegalHold === true;
              case "RecoveryPeriod":
                return retentionCaseSearchConfig.allowDeletedCases === true;
              case "Expired":
                return retentionCaseSearchConfig.allowExpiredCases === true;
              case "ExpiresBefore":
                return retentionCaseSearchConfig.allowExpiresBefore === true;
              case "DeletionRequested":
                return retentionCaseSearchConfig.allowDeletionRequested === true;
              case "Indefinite":
                return retentionCaseSearchConfig.allowIndefinitelyRetained === true;
              case "Retained":
              case "DeletionRequired":
              case "DeletionInProgress":
                return true;
              default:
                // exhaustive check is important to ensure we respect all filters
                value satisfies never;
                return true;
            }
          })();
          if (!isStatusActive) continue;

          retentionFilterOptions.set(value, { value, label });
        }

        if (retentionFilterOptions.size > 0) {
          const STATUS_DISPLAY_NAME = "Case Retention Status";
          const hasExpiresBefore = retentionFilterOptions.has("ExpiresBefore");

          parsed.push(
            {
              displayName: STATUS_DISPLAY_NAME,
              fieldPath: RETENTION_STATUS_FIELD_PATH,
              defaultVisible: true,
              sortable: false,
              filterable: true,
              searchable: false,
              filterConfig: {
                type: "select",
                filterGroup: "retention",
                description: "Case Retention Status",
                options: [...retentionFilterOptions.values()].sort((a, b) => a.label.localeCompare(b.label)),
              },
            },
            {
              displayName: "Case Expiry Date",
              fieldPath: RETENTION_EXPIRY_DATE_FIELD_PATH,
              sortable: false,
              filterable: hasExpiresBefore,
              searchable: false,
              defaultVisible: true,
              filterConfig: {
                type: "date-range",
                allowRange: false,
                filterGroup: "retention",
                description: "Case Expiry Date",
                // Filter won't be shown in the first place
                helpText: hasExpiresBefore
                  ? `Only applicable and required when "${STATUS_DISPLAY_NAME}" is set to "${retentionFilterOptions.get("ExpiresBefore")!.label}"`
                  : undefined,
              },
            },
          );
        }
      }

      return parsed.sort(
        (a, b) => (a.order ?? a.filterConfig?.order ?? Infinity) - (b.order ?? b.filterConfig?.order ?? Infinity),
      );
    },
    availableDialogueRolesConfig(): NonNullable<BrandViewerUIConfig["availableDialogueRoles"]> {
      const config = this.viewerConfig.availableDialogueRoles;
      return Array.isArray(config) ? config : [];
    },
    brandConfig(): Required<NonNullable<State["__brandConfig__"]>> {
      const retention = this.__brandConfig__?.retention ?? {
        authorizedUsersMayDeleteBeforeExpiry: false,
        authorizedUsersMayReduceBelowBrandMinimum: false,
        authorizedUsersMayReduceRetentionDuration: false,
        automaticallyDeleteExpiredCases: false,
        casesMayBeSelectedForIndefiniteRetention: false,
        defaultRetentionDuration: "P0M",
        deletedCaseRecoveryDuration: null,
        maxExtensionDuration: null,
        retentionDurationChangesRequireReason: false,
        twoUserDeletionRequired: false,
        retentionFilters: [],
      };
      return { retention };
    },
  },
  actions: {
    async loadMapping() {
      this.setStatus(ModuleStatus.Loading);
      try {
        const mappings = await brandService.brandMapping();
        this.setMappings(mappings);
        this.setStatus(BrandStatus.MappingLoaded);
      } catch (e) {
        console.error("Error loading brand mapping");
        if (import.meta.env.MODE !== "test") console.error(e);

        this.setStatus(BrandStatus.NoAccess);
        return;
      }
    },
    async onBrandSelected(): Promise<void> {
      if (this.moduleStatus === ModuleStatus.Loading) return;
      this.setStatus(ModuleStatus.Loading);

      if (!this.selectedBrand) {
        this.setStatus(BrandStatus.MissingBrand);
        return;
      }

      if (!this.selectedClient) {
        console.error("Error loading brand. Brand not mapped");
        this.setStatus(BrandStatus.NoAccess);
        return;
      }

      // Do we have any permissions on this brand
      if (Object.keys(useUserPermissionStore().userPermission).length === 0) {
        console.error("Error loading brand. No permissions found");
        this.setStatus(BrandStatus.NoAccess);
        return;
      }

      try {
        this.setStatus(BrandStatus.LoadingConfiguration);

        await this.getLenses();

        // Loaded before the lens is settled, so a brand opened without a lens in the URL (e.g. from an
        // agent link) lands on the lens the user last selected.
        await useUserSessionStore().loadClientNameSession();
        if (this.currentRoute.query.lens == null) {
          const lastUsedLens = useUserSessionStore().brandLastUsedLens?.toLowerCase();
          const lens = this.lenses.find((l) => l.code === lastUsedLens);
          if (lens != null) await this.storeLens(lens);
        }

        if ((this.currentRoute.query.lens as string | undefined)?.toLowerCase() !== this.selectedLens.code) {
          await this.storeLens(this.selectedLens);
        }

        const [configState] = await Promise.allSettled([
          this.getViewerConfig(),
          useSettingsStore().load(),
          useUserSessionStore().saveLastUsedBrand(this.selectedBrand),
          useFiltersStore().load(),
          useBrandSecurityStore().load(),
          this.getDocumentClassifications(),
        ]);

        if (configState.status === "rejected") throw configState.reason;

        await this.getEvidenceCountLabels();
        this.setStatus(ModuleStatus.Ready);
      } catch (e) {
        console.error("Error loading brand");
        if (import.meta.env.MODE !== "test") console.error(e);
        this.setStatus(BrandStatus.NoAccess);
      }
    },
    async getDocumentClassifications(): Promise<void> {
      if (this.selectedBrand == null || this.selectedClient == null) {
        throw { status: 204 };
      }

      const classifications = await brandService.documentClassifications(this.selectedClient, this.selectedBrand);
      this.storeDocumentClassifications(classifications);
    },
    async getViewerConfig(): Promise<void> {
      if (this.selectedBrand == null || this.selectedClient == null) {
        throw { status: 204 };
      }

      const [uiConfig, retentionConfig] = await Promise.all([
        brandClientUiConfigService.Get(this.selectedClient, this.selectedBrand, this.selectedLens.code),
        // Retention configuration is supplementary — don't let a failure here take down the whole brand load,
        // the `brandConfig` getter already falls back to safe defaults when this isn't populated.
        brandConfigService.GetRetentionConfig(this.selectedClient, this.selectedBrand).catch((e: unknown) => {
          console.error("Failed to load retention configuration", e);
          return undefined;
        }),
      ]);

      this.storeViewerConfig(uiConfig);
      if (retentionConfig != null) this.storeBrandConfig("retention", retentionConfig);
    },
    async getEvidenceCountLabels(): Promise<void> {
      try {
        if (this.selectedBrand == null || this.selectedClient == null) {
          throw { status: 204 };
        }

        if (this.featuresConfig?.evidenceCounts === "disabled") return;

        const labels = await recordEvidenceCountService.getAllLabels(this.selectedClient, this.selectedBrand, "record");
        this.storeEvidenceCountLabels(labels);
      } catch (error) {
        if (isApiError(error) && error.status === 204) {
          this.storeEvidenceCountLabels([]);
          return;
        }

        useLoggingStore().Error({
          message: "Failed to load evidence count labels into module",
          data: error as object,
        });
      }
    },
    async getLenses(): Promise<void> {
      if (this.selectedBrand == null || this.selectedClient == null) {
        throw { status: 204 };
      }

      const lenses = await brandLensService.getAll(this.selectedClient, this.selectedBrand);
      this.storeLenses(lenses);
    },
    async selectLens(lens: BrandLens): Promise<void> {
      await this.storeLens(lens);
      // Saved before the reload below, which would otherwise cancel the request.
      await useUserSessionStore()
        .saveLastUsedLens(lens.code)
        .catch((error: unknown) => console.warn("Failed to save the last used lens", error));
      recordReload("lens-selected");
      location.reload();
    },

    // Mutations
    storeDocumentClassifications(payload: BrandDocumentClassification[]): void {
      this.documentClassifications = payload;
    },
    storeBrandConfig<T extends keyof NonNullable<State["__brandConfig__"]>>(
      key: T,
      payload: NonNullable<State["__brandConfig__"]>[T],
    ): void {
      this.__brandConfig__ ??= {};
      this.__brandConfig__[key] = payload;
    },
    storeViewerConfig(payload: BrandViewerUIConfig): void {
      this.__lensConfig__ = payload.lens;
      this.__viewerConfig__ = { ...defaultBrandUIConfig, ...payload, ...payload.lens };
    },
    storeEvidenceCountLabels(payload: string[] | undefined): void {
      this.evidenceCountLabels = payload ?? [];
    },
    setStatus(moduleStatus: BrandStatus | ModuleStatus): void {
      this.moduleStatus = moduleStatus;
    },
    setMappings(payload: { [x: string]: string }): void {
      this.mappings = payload;
    },
    storeLenses(payload: BrandLens[]): void {
      this.lenses = payload
        .filter((l) => l.active)
        .map((l) => ({ ...l, code: l.code.toLowerCase() }))
        .sort((l, r) => {
          // Move active items to the start and sort each active group by their order
          // eslint-disable-next-line @typescript-eslint/strict-boolean-expressions
          const diff = +r.active - +l.active || l.order - r.order;
          if (diff !== 0) return diff;

          // Move default lens to the start of this same active state, same order group state
          if (l.code.toLowerCase() === defaultLens.code.toLowerCase()) return -1;
          else return 1;
        });
    },
    async storeLens(payload: BrandLens): Promise<void> {
      const currentRoute = this.currentRoute;
      const routeLens = ((currentRoute.query.lens as string | undefined) ?? defaultLens.code).toLowerCase();

      if (routeLens == null || routeLens === payload.code.toLowerCase()) return;

      if (payload.code.toLowerCase() === defaultLens.code) {
        await this.router.push({
          ...currentRoute,
          // The "forceLensRemove" query is only used to know when a navigation WITHOUT a lens, coming
          // from a route WITH a lens is intended and should be ignored
          query: { ...currentRoute.query, lens: undefined, forceLensRemove: "" },
        });
      } else {
        await this.router.push({
          ...currentRoute,
          query: { ...currentRoute.query, lens: payload.code },
        });
      }
    },
    async saveLensDefaultLayout(layout: NonNullable<BrandViewerUIConfig>["flexDefaultLayout"] | undefined) {
      if (this.selectedBrand == null || this.selectedClient == null) {
        throw { status: 204 };
      }

      await brandClientUiConfigService.Post(this.selectedClient, this.selectedBrand, this.selectedLens.code, {
        ...this.viewerConfig,
        lens: { ...this.__lensConfig__, flexDefaultLayout: layout },
      });
    },
  },
});
